// MonthView.jsx - Enhanced month view component for date overrides
import React, { useState } from "react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  styled,
  Card,
  Box,
  Paper,
  Chip,
} from "@mui/material";

import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

// Full array of time slots in 15-minute increments
const ALL_TIME_SLOTS = [
  "12:00 am",
  "12:15 am",
  "12:30 am",
  "12:45 am",
  "01:00 am",
  "01:15 am",
  "01:30 am",
  "01:45 am",
  "02:00 am",
  "02:15 am",
  "02:30 am",
  "02:45 am",
  "03:00 am",
  "03:15 am",
  "03:30 am",
  "03:45 am",
  "04:00 am",
  "04:15 am",
  "04:30 am",
  "04:45 am",
  "05:00 am",
  "05:15 am",
  "05:30 am",
  "05:45 am",
  "06:00 am",
  "06:15 am",
  "06:30 am",
  "06:45 am",
  "07:00 am",
  "07:15 am",
  "07:30 am",
  "07:45 am",
  "08:00 am",
  "08:15 am",
  "08:30 am",
  "08:45 am",
  "09:00 am",
  "09:15 am",
  "09:30 am",
  "09:45 am",
  "10:00 am",
  "10:15 am",
  "10:30 am",
  "10:45 am",
  "11:00 am",
  "11:15 am",
  "11:30 am",
  "11:45 am",
  "12:00 pm",
  "12:15 pm",
  "12:30 pm",
  "12:45 pm",
  "01:00 pm",
  "01:15 pm",
  "01:30 pm",
  "01:45 pm",
  "02:00 pm",
  "02:15 pm",
  "02:30 pm",
  "02:45 pm",
  "03:00 pm",
  "03:15 pm",
  "03:30 pm",
  "03:45 pm",
  "04:00 pm",
  "04:15 pm",
  "04:30 pm",
  "04:45 pm",
  "05:00 pm",
  "05:15 pm",
  "05:30 pm",
  "05:45 pm",
  "06:00 pm",
  "06:15 pm",
  "06:30 pm",
  "06:45 pm",
  "07:00 pm",
  "07:15 pm",
  "07:30 pm",
  "07:45 pm",
  "08:00 pm",
  "08:15 pm",
  "08:30 pm",
  "08:45 pm",
  "09:00 pm",
  "09:15 pm",
  "09:30 pm",
  "09:45 pm",
  "10:00 pm",
  "10:15 pm",
  "10:30 pm",
  "10:45 pm",
  "11:00 pm",
  "11:15 pm",
  "11:30 pm",
  "11:45 pm",
];

const ChatContainer = styled(Grid)(({ theme }) => ({
  width: "100%",
  height: "100%",
}));

const ChatSidebar = styled(Grid)(({ theme }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const MonthView = ({ onAddOverride, initialOverride = null }) => {
  // Initialize state with values from initialOverride if provided
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialOverride) {
      // If initialOverride has a date string, convert it to dayjs object
      return typeof initialOverride.date === "string"
        ? dayjs(initialOverride.date)
        : initialOverride.date;
    }
    return dayjs(); // Default to current date
  });

  const [isUnavailable, setIsUnavailable] = useState(() => {
    if (initialOverride) {
      return (
        initialOverride.isUnavailable ||
        initialOverride.timeSlots[0]?.timeStart === "Unavailable all day"
      );
    }
    return false; // Default to false
  });

  const [timeSlots, setTimeSlots] = useState(() => {
    if (initialOverride && !initialOverride.isUnavailable) {
      // If we have an initial override with time slots, use them
      const slots = initialOverride.timeSlots.filter(
        (slot) =>
          slot.timeStart !== "Unavailable all day" &&
          slot.timeStart &&
          slot.timeEnd
      );

      // If we have valid slots, use them, otherwise use default empty slot
      return slots.length > 0 ? slots : [{ timeStart: "", timeEnd: "" }];
    }
    return [{ timeStart: "", timeEnd: "" }]; // Default to one empty slot
  });

  // Button text changes based on whether we're editing
  const buttonText = initialOverride ? "Update Override" : "Add Override";

  const addTimeSlot = () => {
    setTimeSlots([...timeSlots, { timeStart: "", timeEnd: "" }]);
  };

  const removeTimeSlot = (index) => {
    const updatedTimeSlots = [...timeSlots];
    updatedTimeSlots.splice(index, 1);
    setTimeSlots(updatedTimeSlots);
  };

  const handleAddOrUpdateOverride = () => {
    if (selectedDate) {
      if (isUnavailable) {
        // When marking a day as unavailable, we need valid values for both timeStart and timeEnd
        onAddOverride({
          date: selectedDate,
          timeSlots: [
            {
              timeStart: "Unavailable all day",
              timeEnd: "Unavailable all day",
            },
          ],
          isUnavailable: true,
        });
      } else {
        // Filter out incomplete time slots
        const validTimeSlots = timeSlots.filter(
          (slot) => slot.timeStart && slot.timeEnd
        );

        if (validTimeSlots.length > 0) {
          onAddOverride({
            date: selectedDate,
            timeSlots: validTimeSlots,
            isUnavailable: false,
          });
        } else {
          // Show error or warning that no valid time slots were provided
          console.warn("No valid time slots provided");
        }
      }
    }
  };

  // Validate if we can add an override (either unavailable or has valid time slots)
  const canAddOverride =
    isUnavailable || timeSlots.some((slot) => slot.timeStart && slot.timeEnd);

  return (
    <ChatContainer container component={Paper} elevation={0} variant="outlined">
      <ChatSidebar item xs={12} md={6} lg={5}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Select Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DateCalendar"]}>
              <DemoItem>
                <DateCalendar
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue || dayjs())}
                  style={{ width: "100%" }}
                />
              </DemoItem>
            </DemoContainer>
          </LocalizationProvider>
        </Box>
      </ChatSidebar>

      <ChatSidebar item xs={12} md={6} lg={7} style={{ border: "0" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {selectedDate.format("dddd, MMMM D, YYYY")}
          </Typography>

          <FormGroup sx={{ mb: 3, mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUnavailable}
                  onChange={(e) => {
                    setIsUnavailable(e.target.checked);
                    if (e.target.checked) {
                      // Clear any time slots if marking as unavailable
                      setTimeSlots([{ timeStart: "", timeEnd: "" }]);
                    }
                  }}
                />
              }
              label="Mark as unavailable (All day)"
            />
            {isUnavailable && (
              <Chip
                label="You will not be bookable on this date"
                color="error"
                variant="outlined"
                size="small"
                sx={{ ml: 4, mt: 1, maxWidth: "fit-content" }}
              />
            )}
          </FormGroup>

          {!isUnavailable && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Which hours are you free?
              </Typography>

              {timeSlots.map((slot, index) => (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  key={index}
                  sx={{ mb: 2 }}
                >
                  <Grid item md={5} xs={5}>
                    <Select
                      fullWidth
                      value={slot.timeStart || ""}
                      onChange={(e) => {
                        const updatedTimeSlots = [...timeSlots];
                        updatedTimeSlots[index].timeStart = e.target.value;
                        setTimeSlots(updatedTimeSlots);
                      }}
                      size="small"
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Start time
                      </MenuItem>
                      {ALL_TIME_SLOTS.map((time) => (
                        <MenuItem key={`start-${time}-${index}`} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  <Grid item md={5} xs={5}>
                    <Select
                      fullWidth
                      value={slot.timeEnd || ""}
                      onChange={(e) => {
                        const updatedTimeSlots = [...timeSlots];
                        updatedTimeSlots[index].timeEnd = e.target.value;
                        setTimeSlots(updatedTimeSlots);
                      }}
                      size="small"
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        End time
                      </MenuItem>
                      {ALL_TIME_SLOTS.map((time) => (
                        <MenuItem key={`end-${time}-${index}`} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>

                  <Grid item md={2} xs={2} container spacing={1}>
                    <Grid item>
                      <IconButton
                        size="small"
                        onClick={() => removeTimeSlot(index)}
                        disabled={timeSlots.length === 1}
                        sx={{ p: 0.5 }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Grid>

                    {index === timeSlots.length - 1 && (
                      <Grid item>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={addTimeSlot}
                          sx={{ p: 0.5 }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ))}
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              onClick={handleAddOrUpdateOverride}
              variant="contained"
              color="primary"
              disabled={!canAddOverride}
            >
              {buttonText}
            </Button>
          </Box>
        </Box>
      </ChatSidebar>
    </ChatContainer>
  );
};

export default MonthView;
